<template lang="pug">
.single-listing-page
    section.single-listing-banner(:style="coverImageStyle")
        .listing-user-header
            .container
                .row.align-items-center
                    .col-5.col-md-3
                        .profile-image-container(:style="profileImageStyle")
                    .col-7.col-md-9
                        .row.color-white
                            .col-lg-8
                                h2 {{ profile.name }}
                                address
                                    i.bi.bi-geo-alt
                                    |  {{ location }}
                            .col-lg-4.text-right
                                .rating-box
                                    .rating-review {{ profile.rating || '-' }}
                                    .rating-stars
                                        rating-stars(:rating="profile.rating" all)
                                        span.total-reviews {{ profile.review_count }} {{ profile.review_count | pluralize('review') }}
    section.breadcrumbs
        .container
            .row
                .col-12
                    ul.list-inline.m-0
                        li.list-inline-item
                            a(href="#") Home
                            i.bi.bi-chevron-right
                        li.list-inline-item
                            a(href="#") {{ city }}
                            i.bi.bi-chevron-right
                        li.list-inline-item Game Master {{ profile.name }}
    section.content-section.single-listing.pt-0.pb-60
        .container
            .row
                .col-lg-8.col-xl-7
                    .card(v-if="profile.about")
                        h5.card-header Description
                        .card-body
                            p {{ profile.about }}
                    .card
                        h5.card-header Games & Features
                        .card-body
                            template(v-for="game in games")
                                h6 {{ game.title }}
                                ul.list-inline.mb-0
                                    li.list-inline-item(v-for="addon in game.addons")
                                        img(:src="`${baseUri}/images/client/lairdnd/upgrades/${addon.icon}.svg`" alt="")
                                        | {{ addon.title }}
                                p {{ game.description }}
                    .card.available-dates-card
                        h5.card-header
                            .btn.btn-outline-primary(@click="calendarLeft" :class="{ disabled: !calendarCanGoLeft }")
                                svg-icon(icon-id="arrow-left")
                            | Available Dates
                            .btn.btn-outline-primary(@click="calendarRight" :class="{ disabled: !calendarCanGoRight }")
                                svg-icon(icon-id="arrow-right")
                        .card-body
                            .row
                                .col-md-6.calendar-container
                                    b-calendar(ref="leftCalendar"
                                        v-model="selectedDateLeft" @selected="dateSelectedLeft" :hide-header="true" :min="minDate" :max="maxDate" no-highlight-today :date-info-fn="dateClassLeft" :date-disabled-fn="dateDisabled")
                                .col-md-6.calendar-container
                                    b-calendar(ref="rightCalendar" :initial-date="initialDateRight"
                                        v-model="selectedDateRight" @selected="dateSelectedRight" :hide-header="true" :min="minDate" :max="maxDate" no-highlight-today :date-info-fn="dateClassRight" :date-disabled-fn="dateDisabled")
                    .card.reviews-list(v-if="reviews && reviews.length")
                        h5.card-header
                            .title-box Reviews for
                                .title {{ profile.name }}
                            .divider
                            .rating-box
                                .rating-review {{ profile.rating }}
                                .rating-stars
                                    rating-stars(:rating="profile.rating" all)
                        .card-body
                            ul
                                li(v-for="review in reviews")
                                    user-profile-image(:user="review.user.data" size="sm")
                                    .review-box
                                        .row.align-items-center
                                            .col-sm-6
                                                .review-title {{ review.user.data.name }}
                                                    span
                                                        i.bi.bi-calendar
                                                        time(:datetime="review.reviewed_at") {{ review.reviewed_at | datetime }}
                                            .col-sm-6.text-right.small-review
                                                .rating-box
                                                    .rating-stars
                                                        rating-stars(:rating="review.rating" all)
                                                    .rating-review {{ review.rating }}
                                        p ” {{ review.message }} “
                    .card(v-if="reviewable")
                        h5.card-header Add Review
                        .card-body
                            .row.align-items-center
                                .col-sm-6
                                    user-profile-image(:user="authUser" size="sm")
                                    | &nbsp; {{ authUser.name }}
                                .col-sm-6.text-right.small-review
                                    .rating-box
                                        .rating-stars
                                            rating-stars(:rating="submitRating" @click="submitRating = $event.star" all)
                                        .rating-review {{ submitRating || '-' }}
                            .row
                                .col-12
                                    textarea.form-control.mt-3(v-model="reviewMessage")
                                    .text-right
                                        .btn.btn-danger.btn-lg.mt-3.mb-2(@click="postReview") Post Review
                .col-xl-1.d-lg-none.d-xl-block
                .col-lg-4.col-xl-4
                    .card.right-side-card.hourly-price-card
                        h5.card-header Hourly Price
                        .card-body
                            .two-cols
                                .left Price:
                                .right ${{ rate }} per hour

                    booking(v-if="showBookingForm" :profile="profile" @book="book" ref="bookingForm")
                    payment(v-if="showPaymentForm" :profile="profile" :booking="booking" @paid="paid" ref="paymentForm")
                    thank-you(v-if="showThankYou" :profile="profile" :order="order")

    script(type="application/ld+json" v-html="richSnippet")

</template>

<script>
import { mapGetters } from 'vuex'
import Booking from './booking'
import Payment from './payment'
import ThankYou from './thank-you'
import Datetime from '../../filters/datetime'
import moment from 'moment'
import { get$ } from 'utils/vue/lib/utils'
import { BCalendar } from 'bootstrap-vue'

const MIN_HOURS = 2

export default {
    name: 'game-master-profile',

    components: {
        Booking,
        Payment,
        ThankYou,
        BCalendar,
    },

    filters: {
        Datetime,
    },

    props: {
        profile: {
            type: Object,
            required: true,
        },
    },

    metaInfo() { return {
        title: this.metaTitle,
        meta: [
            { name: 'description', content: this.metaDescription },
            { name: 'og:title', content: `${this.metaTitle} - Lair DND` },
            { name: 'og:description', content: this.metaDescription },
        ],
    }},

    data() { return {
        booking: null,
        order: null,
        reviewMessage: '',
        submitRating: 0,
        availableDatesMonth: moment().toDate(),
        selectedDate: null,
        selectedDateLeft: null,
        selectedDateRight: null,
        disabledDates: [],
        reviews: null,
        reviewable: false,
    }},

    async created() {
        const { data: { data: reviews } } = await axios.get(`/api/customer/game-master-reservation-product/${this.product.id}/review?include=user`)
        this.reviews = reviews

        if (this.$store.getters.authCheck) {
            await this.checkIfReviewable()
        }

        await this.retrieveDisabledDates()
    },

    computed: {
        ...mapGetters(['authUser', 'authCheck']),

        metaTitle() {
            return `${this.profile.name} Game Masters in ${this.location}`
        },
        metaDescription() {
            let description = `${this.profile.name} Game Masters in ${this.location} book now and lets play your favorite games.`

            if (this.profile.about) {
                description += ' ' + this.profile.about
            }

            return description.substring(0, 160)
        },

        coverImageStyle() {
            return this.profile.cover_image_lg ?
                { 'background-image': `url(${this.baseUri}${this.profile.cover_image_lg})` } :
                {}
        },
        profileImage() {
            return this.profile.profile_image_lg || '/images/client/lairdnd/default-profile-image.png'
        },
        profileImageStyle() {
            return { 'background-image': `url(${this.baseUri}${this.profileImage})` }
        },
        product() {
            return this.profile.product.data
        },
        rate() {
            return this.product.rates[0].price
        },
        games() {
            const games = []
            const upgrades = Object.fromEntries(this.product.upgrades.map(upgrade => [upgrade.name, upgrade]))

            const gameTitles = Object.fromEntries(window.addiesaas.lairdnd.games.map(game => {
                return [game.name, game.title]
            }))

            for (let game of this.product.games) {
                if (gameTitles[game.name]) {
                    game = Object.assign({}, game)
    
                    game.title = gameTitles[game.name]
    
                    const addons = []
                    for (const addon of game.addons) {
                        const slug = addon //.replace('3-d', '3d')
                        if (slug in upgrades) {
                            addons.push(upgrades[slug])
                        }
                    }
                    game.addons = addons
                    games.push(game)
                }
            }
            return games
        },
        city() {
            return this.product.location.city
        },
        location() {
            return `${this.product.location.city} ${this.product.location.state} ${this.product.location.country}`
        },
        bookable() {
            return !this.profile.owner
        },
        showBookingForm() {
            return !this.booking && !this.order
        },
        showPaymentForm() {
            return !!this.booking && !this.order
        },
        showThankYou() {
            return !!this.order
        },

        calendarMonth() {
            return moment(this.availableDatesMonth).toDate()
        },
        calendarCanGoLeft() {
            return moment(this.minDate).startOf('month') < moment(this.availableDatesMonth).startOf('month')
        },
        calendarCanGoRight() {
            return moment(this.maxDate).startOf('month') > moment(this.availableDatesMonth).add(1, 'M').startOf('month')
        },
        initialDateRight() {
            return moment(this.availableDatesMonth).add(1, 'M').toDate()
        },
        minDate() {
            return moment().add(this.product.hours_in_advance, 'h').toDate()
        },
        maxDate() {
            return moment().add(this.product.days_in_future, 'd').toDate()
        },
        richSnippet() {
            return {
                "@context": "https://schema.org",
                "@type": "LocalBusiness",
                address: {
                    "@type": "PostalAddress",
                    addressCountry: this.product.location.country,
                    addressRegion: this.product.location.state,
                    addressLocality: this.product.location.city,
                    streetAddress: this.product.location.street,
                    postalCode: this.product.location.zip,
                },
                name: this.profile.name,
                description: this.profile.about,
                photo: this.profile.profile_image ? `${this.baseUri}${this.profile.profile_image}` : null,
                aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: this.profile.rating,
                    reviewCount: this.profile.review_count,
                },
                areaServed: {
                    "@type": "GeoCircle",
                    geoMidpoint: {
                        "@type": "GeoCoordinates",
                        latitude: this.product.location_latitude,
                        longitude: this.product.location_longitude,
                    },
                    geoRadius: this.product.location_radius * 1000,
                },
                reviews: this.reviewsRichSnippet,
            }
        },
        reviewsRichSnippet() {
            if (this.reviews) {
                return this.reviews.map(review => ({
                    "@type": "Review",
                    name: review.user.data.name,
                    author: review.user.data.name,
                    reviewBody: review.message,
                    reviewRating: {
                        "@type": "Rating",
                        ratingValue: review.rating,
                    },
                    datePublished: review.reviewed_at,
                }))
            }
        },
    },

    methods: {
        book(booking) {
            this.booking = booking

            this.$nextTick(() => {
                const el = get$('.payment-card')
                if (el) {
                    let top = el.offset().top
                    window.scrollTo({ top, behavior: 'smooth' })
                }
            })
        },
        paid(order) {
            this.order = order
        },
        dateClass(ymd, date, offset = 0) {
            if (moment(date).month() != moment(this.calendarMonth).add(offset, 'M').month()) {
                return 'not-same-month'
            }
            return ''
        },
        dateClassLeft(ymd, date) {
            return this.dateClass(ymd, date)
        },
        dateClassRight(ymd, date) {
            return this.dateClass(ymd, date, 1)
        },
        dateDisabled(ymd, date) {
            return this.disabledDates.includes(ymd)
        },
        calendarLeft() {
            if (this.calendarCanGoLeft) {
                const availableDatesMonth = this.availableDatesMonth
                this.availableDatesMonth = moment(this.availableDatesMonth).add(-1, 'M').toDate()
                this.$refs.leftCalendar.gotoPrevMonth()
                this.$refs.rightCalendar.gotoPrevMonth()
                if (this.selectedDateLeft && moment(this.selectedDateLeft).isSame(availableDatesMonth, 'month')) {
                    this.selectedDateRight = this.selectedDateLeft
                }
                this.retrieveDisabledDates()
            }
        },
        calendarRight() {
            if (this.calendarCanGoRight) {
                this.availableDatesMonth = moment(this.availableDatesMonth).add(1, 'M').toDate()
                this.$refs.leftCalendar.gotoNextMonth()
                this.$refs.rightCalendar.gotoNextMonth()
                if (this.selectedDateRight && moment(this.selectedDateRight).isSame(this.availableDatesMonth, 'month')) {
                    this.selectedDateLeft = this.selectedDateRight
                }
                this.retrieveDisabledDates()
            }
        },
        dateSelectedLeft(ymd, date) {
            this.setDate(date)
            this.selectedDateLeft = date
            this.selectedDateRight = null
        },
        dateSelectedRight(ymd, date) {
            this.setDate(date)
            this.selectedDateLeft = null
            this.selectedDateRight = date
        },
        setDate(date) {
            this.selectedDate = date
            this.booking = null
            this.order = null
            this.$refs.bookingForm.setDate(date)
        },
        async retrieveDisabledDates() {
            let { data: { unavailability }} = await axios.post('/api/customer/reservation/unavailability-dates', {
                product_id: this.product.id,
                participants: this.product.min_participants,
                slots: MIN_HOURS,
                date_from: moment(this.availableDatesMonth).startOf('month').format('Y-MM-DD'),
                date_to: moment(this.availableDatesMonth).add(1, 'months').endOf('month').format('Y-MM-DD'),
                time_slot_size: 60,
            })

            this.disabledDates = unavailability
        },

        async checkIfReviewable() {
            const { data: reviewable } = await axios.get(`/api/customer/game-master-reservation-product/${this.product.id}/reviewable`)
            this.reviewable = reviewable
        },
        async postReview() {
            const payload = {
                message: this.reviewMessage,
                rating: this.submitRating,
            }

            const { data: { data: review } } = await axios.post(`/api/customer/game-master-reservation-product/${this.product.id}/review`, payload)

            review.user = { data: this.authUser }
            this.reviews.unshift(review)

            this.profile.rating = Math.round(this.reviews.reduce((a, c) => a + c.rating, 0) / this.reviews.length * 10) / 10
            this.profile.review_count = this.reviews.length

            this.checkIfReviewable()
            this.submitRating = 0
            this.reviewMessage = ''
        },
    }
}
</script>
